<template>
  <div class="sp-panel">
    <div v-if="!hasContent" class="sp-empty">
      <span class="sp-empty-text">Substack post not generated yet.</span>
    </div>

    <template v-else>
      <div class="sp-meta">
        <h3 class="sp-title">{{ post.title }}</h3>
        <div v-if="post.subtitle" class="sp-subtitle">{{ post.subtitle }}</div>
        <div class="sp-meta-row">
          <span v-if="readTime" class="sp-chip">{{ readTime }} min read</span>
          <span v-if="wordCount" class="sp-chip sp-chip-muted">{{ wordCount }} words</span>
          <span v-for="tag in (post.tags || [])" :key="tag" class="sp-tag">{{ tag }}</span>
        </div>
      </div>

      <div v-if="post.cover_html" class="sp-cover">
        <div class="sp-section-label">Cover image (1456×819 · Substack OG)</div>
        <div class="sp-cover-wrap-outer">
          <div class="sp-cover-wrap">
            <iframe
              ref="coverFrame"
              :srcdoc="post.cover_html"
              sandbox="allow-same-origin"
              referrerpolicy="no-referrer"
            ></iframe>
          </div>
        </div>
        <div class="sp-cover-meta">Preview rendered at 50% scale; download is full 1456×819.</div>
        <div class="sp-cover-actions">
          <el-button size="small" :loading="downloading" icon="el-icon-download" type="primary" plain @click="downloadCoverPng">Download cover as PNG</el-button>
          <el-button size="small" icon="el-icon-document-copy" @click="copy(post.cover_html, 'Cover HTML')">Copy HTML</el-button>
        </div>
      </div>
      <div v-else class="sp-cover sp-cover-empty">
        <div class="sp-section-label">Cover image</div>
        <span class="sp-cover-empty-text">Not generated yet — see <code>Collection Substack Post Procedures.md</code> §3.8 for the cover_html spec.</span>
      </div>

      <div v-if="post.thesis" class="sp-thesis">
        <div class="sp-section-label">Thesis</div>
        <blockquote>{{ post.thesis }}</blockquote>
      </div>

      <div v-if="post.featured_pull_quote" class="sp-pull">
        <div class="sp-section-label">Featured pull-quote</div>
        <blockquote class="sp-pull-q">{{ post.featured_pull_quote }}</blockquote>
      </div>

      <div v-if="post.body_html" class="sp-body">
        <div class="sp-section-label">Body preview</div>
        <div class="sp-body-preview">
          <iframe :srcdoc="bodyHtmlWrapped" sandbox="" referrerpolicy="no-referrer"></iframe>
        </div>
      </div>

      <div v-if="footerLinks.length" class="sp-footer-links">
        <div class="sp-section-label">Footer links</div>
        <ul>
          <li v-for="(link, i) in footerLinks" :key="i">
            <a :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
            <span class="sp-link-url">{{ link.url }}</span>
          </li>
        </ul>
      </div>

      <div class="sp-actions">
        <el-button size="small" icon="el-icon-document-copy" :disabled="!post.body_markdown" @click="copy(post.body_markdown, 'Markdown')">Copy markdown</el-button>
        <el-button size="small" icon="el-icon-document-copy" :disabled="!post.body_html" @click="copy(post.body_html, 'HTML')">Copy HTML</el-button>
        <el-button size="small" icon="el-icon-document-copy" :disabled="!post.title" @click="copy(post.title, 'Title')">Copy title</el-button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "SubstackPostPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  data() {
    return {
      downloading: false,
    };
  },
  computed: {
    post() {
      return this.collectionForm && this.collectionForm.substack_post
        ? this.collectionForm.substack_post
        : null;
    },
    hasContent() {
      return !!(this.post && (this.post.title || this.post.body_markdown));
    },
    readTime() {
      return (this.post && this.post.estimated_read_minutes) || 0;
    },
    wordCount() {
      const md = this.post && this.post.body_markdown;
      if (!md) return 0;
      return md.split(/\s+/).filter(Boolean).length;
    },
    footerLinks() {
      return (this.post && Array.isArray(this.post.footer_links)) ? this.post.footer_links : [];
    },
    bodyHtmlWrapped() {
      const html = this.post.body_html || "";
      return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Charter,Georgia,"Times New Roman",serif;font-size:16px;line-height:1.65;color:#1a1a1a;margin:18px;max-width:680px;}p{margin:0 0 16px}h2{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;font-size:22px;font-weight:600;margin:28px 0 14px}h3{font-size:18px;margin:22px 0 10px}blockquote{margin:18px 0;padding:10px 18px;border-left:3px solid #555;color:#444;font-style:italic;background:#fafafa;}a{color:#0a6cdf}strong{color:#000}</style></head><body>${html}</body></html>`;
    },
  },
  methods: {
    async ensureHtml2canvas() {
      if (window.html2canvas) return window.html2canvas;
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        s.crossOrigin = "anonymous";
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load html2canvas"));
        document.head.appendChild(s);
      });
      return window.html2canvas;
    },
    async downloadCoverPng() {
      if (!this.post || !this.post.cover_html) return;
      this.downloading = true;
      try {
        const html2canvas = await this.ensureHtml2canvas();
        const iframe = this.$refs.coverFrame;
        if (!iframe) throw new Error("cover frame not found");
        const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
        if (!doc || !doc.body) throw new Error("cover content not loaded");
        const canvas = await html2canvas(doc.body, {
          width: 1456,
          height: 819,
          windowWidth: 1456,
          windowHeight: 819,
          scale: 2,
          backgroundColor: null,
          useCORS: true,
        });
        const dataUrl = canvas.toDataURL("image/png");
        const slug = (this.post.title || "untitled")
          .slice(0, 60)
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .toLowerCase() || "untitled";
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `substack-cover-${slug}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$message.success("Cover downloaded");
      } catch (e) {
        console.error(e);
        this.$message.error("Cover download failed: " + (e.message || e));
      } finally {
        this.downloading = false;
      }
    },
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} copied`))
          .catch(() => { fallback(); this.$message.success(`${label} copied`); });
      } else {
        fallback();
        this.$message.success(`${label} copied`);
      }
    },
  },
};
</script>

<style scoped>
.sp-panel { padding: 10px 4px; }
.sp-empty { padding: 24px; text-align: center; color: #909399; font-style: italic; }
.sp-meta { padding: 6px 0 14px; border-bottom: 1px solid #f0f2f5; }
.sp-title { margin: 0 0 4px; font-size: 20px; font-weight: 600; color: #303133; line-height: 1.3; }
.sp-subtitle { font-size: 14px; color: #606266; margin-bottom: 8px; }
.sp-meta-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.sp-chip { display: inline-block; padding: 2px 8px; background: #f0f9eb; color: #67c23a; border-radius: 3px; font-size: 12px; }
.sp-chip-muted { background: #f4f4f5; color: #909399; }
.sp-tag { display: inline-block; padding: 2px 8px; background: #ecf5ff; color: #409eff; border-radius: 3px; font-size: 12px; }
.sp-section-label { font-size: 12px; color: #909399; text-transform: uppercase; letter-spacing: 0.06em; margin: 14px 0 6px; }
.sp-thesis blockquote, .sp-pull-q { margin: 0; padding: 10px 14px; border-left: 3px solid #409eff; background: #f7faff; color: #303133; font-style: italic; font-size: 14px; }
.sp-pull-q { border-left-color: #67c23a; background: #f0faf2; font-weight: 500; }
.sp-body-preview { width: 100%; height: 520px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #fff; }
.sp-body-preview iframe { width: 100%; height: 100%; border: 0; }
.sp-cover { padding: 8px 0 14px; border-bottom: 1px solid #f0f2f5; }
.sp-cover-wrap-outer { max-width: 100%; overflow: auto; }
.sp-cover-wrap { width: 728px; height: 410px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #fff; }
.sp-cover-wrap iframe { width: 1456px; height: 819px; transform: scale(0.5); transform-origin: top left; border: 0; display: block; }
.sp-cover-meta { font-size: 11px; color: #c0c4cc; padding-top: 6px; }
.sp-cover-actions { display: flex; gap: 8px; padding-top: 10px; }
.sp-cover-empty { padding: 8px 0 14px; }
.sp-cover-empty-text { font-size: 13px; color: #909399; font-style: italic; }
.sp-cover-empty-text code { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #606266; background: #f4f4f5; padding: 1px 4px; border-radius: 2px; }
.sp-footer-links ul { list-style: none; padding: 0; margin: 0; }
.sp-footer-links li { padding: 4px 0; }
.sp-footer-links a { color: #409eff; margin-right: 10px; }
.sp-link-url { font-size: 12px; color: #909399; }
.sp-actions { display: flex; gap: 10px; padding-top: 14px; border-top: 1px solid #f0f2f5; margin-top: 14px; }
</style>
